import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, Loader2, Ruler, Package, Truck, Sparkles, ShoppingBag, Minus, Plus, Info } from "lucide-react";
import { toast } from "sonner";
import { api, whatsappLink } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import V2ProductCard from "@/v2/ProductCard";

export default function V2ProductDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    setLoading(true); setActive(0); setQty(1); setSize(null);
    api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => setData({ product: null, related: [] })).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#b85742]" /></div>;
  const p = data?.product;
  if (!p) return (
    <div className="max-w-3xl mx-auto text-center py-32 px-6">
      <h1 className="font-serif-warm text-5xl">This piece isn't here.</h1>
      <Link to="/collections" className="mt-8 inline-flex v2-btn-primary">Browse the collection</Link>
    </div>
  );

  const variants = (p.variants || []).filter((v) => v.enabled);
  const variant = variants.find((v) => v.size === size) || variants.find((v) => v.stock > 0) || variants[0] || null;
  const price = variant ? variant.price : p.price_from;
  const mrp = variant ? variant.mrp : p.mrp;
  const soldOut = !!variant && variant.stock <= 0;
  const discount = mrp && mrp > price ? Math.round((1 - price / mrp) * 100) : 0;
  const wa = whatsappLink(`Hi Iryaa Homes, I'd like to enquire about "${p.name}"${variant ? ` in size ${variant.size} ft` : ""} (₹${price.toLocaleString("en-IN")}).`);
  const gallery = p.gallery || [p.image];

  const onAdd = () => {
    if (soldOut) { toast.error(`Size ${variant.size} ft is sold out`); return; }
    add(p, qty, variant);
    toast.success(`${p.name}${variant ? ` (${variant.size} ft)` : ""} × ${qty} added to basket`);
  };

  return (
    <div data-testid="v2-product-detail" className="pb-24">
      {/* Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-8 text-[11px] uppercase tracking-[0.22em] text-[#7a7466]">
        <Link to="/" className="v2-link">Home</Link> / <Link to={`/collections?cat=${p.category}`} className="v2-link">{p.category.replace("-", " ")}</Link> / <span className="text-[#1a1a1a]">{p.name.slice(0, 30)}</span>
      </div>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/5] overflow-hidden bg-[#f2ede2] group">
            <img src={gallery[active]} alt={p.name} data-testid="v2-pd-main-image" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
            {discount > 0 && <div className="absolute top-5 left-5 v2-badge-terra">{discount}% off</div>}
          </div>
          <div className="mt-3 grid grid-cols-5 gap-2">
            {gallery.slice(0, 5).map((g, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`v2-pd-thumb-${i}`} className={`aspect-square overflow-hidden border-2 transition-colors ${active === i ? "border-[#1a1a1a]" : "border-transparent hover:border-[#c9c0ae]"}`}>
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5">
          <div className="overline-v2">{p.category.replace("-", " ")}</div>
          <h1 className="mt-4 font-serif-warm text-4xl md:text-5xl leading-[1.05] tracking-tight">{p.name}</h1>
          <p className="mt-5 text-[15px] text-[#4a4640] leading-relaxed font-light">{p.story}</p>

          <div className="mt-8 flex items-baseline gap-4">
            <div className="font-serif-warm text-3xl text-[#1a1a1a]" data-testid="v2-pd-price">₹{price.toLocaleString("en-IN")}</div>
            {mrp && <div className="text-[15px] text-[#7a7466] line-through" data-testid="v2-pd-mrp">₹{mrp.toLocaleString("en-IN")}</div>}
            {discount > 0 && <div className="text-[11px] uppercase tracking-[0.22em] text-[#b85742]">You save ₹{(mrp - price).toLocaleString("en-IN")}</div>}
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#7a7466]">Inclusive of taxes · {p.lead_time || "Dispatched in 3–5 working days"}</div>

          {variants.length > 0 && (
            <div className="mt-8">
              <label htmlFor="v2-pd-size" className="overline-v2 text-[10px]">Select size (feet)</label>
              <select
                id="v2-pd-size"
                value={variant.size}
                onChange={(e) => { setSize(e.target.value); setQty(1); }}
                data-testid="v2-pd-size-select"
                className="mt-2 w-full h-12 border border-[#1a1a1a] bg-transparent px-4 text-[15px] outline-none"
              >
                {variants.map((v) => (
                  <option key={v.size} value={v.size} disabled={v.stock <= 0}>{v.size} ft{v.stock <= 0 ? " — sold out" : ""}</option>
                ))}
              </select>
            </div>
          )}

          {/* Dimensions strip */}
          <div className="mt-8 grid grid-cols-2 gap-6 border-t border-[#e4ded2] pt-6 text-[13px]">
            <div>
              <div className="flex items-center gap-2 text-[#7a7466]"><Ruler size={13} /><span className="overline-v2 text-[10px]">Size</span></div>
              <div className="mt-2 font-serif-warm text-lg text-[#1a1a1a]">{variant ? `${variant.size} ft` : p.dimensions}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[#7a7466]"><Package size={13} /><span className="overline-v2 text-[10px]">Material</span></div>
              <div className="mt-2 font-serif-warm text-lg text-[#1a1a1a] capitalize">{p.material?.replace("_", " ")}</div>
            </div>
          </div>

          {/* Qty + CTA */}
          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center border border-[#1a1a1a]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="v2-pd-qty-minus" className="w-11 h-14 flex items-center justify-center"><Minus size={13} /></button>
              <div className="w-10 text-center text-[15px]">{qty}</div>
              <button onClick={() => setQty(variant ? Math.min(variant.stock, qty + 1) : qty + 1)} data-testid="v2-pd-qty-plus" className="w-11 h-14 flex items-center justify-center"><Plus size={13} /></button>
            </div>
            <button onClick={onAdd} data-testid="v2-pd-add-to-cart" className="flex-1 v2-btn-primary justify-center">
              <ShoppingBag size={13} /> Add to Basket
            </button>
          </div>
          <a href={wa} target="_blank" rel="noreferrer" data-testid="v2-pd-whatsapp" className="mt-3 v2-btn-outline w-full justify-center">
            Enquire on WhatsApp
          </a>

          {/* Trust strip */}
          <div className="mt-6 flex items-start gap-2 text-[11px] uppercase tracking-[0.2em] text-[#7a7466]">
            <Truck size={13} /> Free shipping above ₹1,000 · Secure Razorpay payment
          </div>

          {/* Features */}
          {p.features?.length > 0 && (
            <div className="mt-8">
              <div className="overline-v2">Features</div>
              <ul className="mt-3 grid grid-cols-1 gap-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] text-[#4a4640]">
                    <Check size={13} className="mt-1 text-[#b85742] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Handmade disclaimer + care */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-14 border-t border-[#e4ded2] grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="overline-v2">Care & keeping</div>
          <h2 className="mt-4 font-serif-warm text-3xl md:text-4xl leading-tight max-w-md">A handmade piece rewards patience.</h2>
          <div className="mt-6 border-l-2 border-[#c9ac93] pl-4 text-[13px] text-[#4a4640] italic leading-relaxed font-light">
            <Info size={14} className="text-[#b85742] mb-2" />
            Because every piece is handcrafted, slight variations in colour, texture and dimensions may occur. These differences are part of the character and authenticity of a handmade product.
          </div>
        </div>
        <ol className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {(p.care || []).map((c, i) => (
            <li key={i} className="border-t border-[#1a1a1a] pt-4">
              <div className="font-serif-warm text-2xl text-[#b85742]">0{i + 1}</div>
              <p className="mt-2 text-[14px] text-[#4a4640] font-light leading-relaxed">{c}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Custom CTA */}
      <section className="bg-[#f2ede2]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="overline-v2">Want this — but in your colours?</div>
            <h3 className="mt-3 font-serif-warm text-3xl md:text-4xl leading-tight">Commission a bespoke version in our Custom Studio.</h3>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/custom" className="v2-btn-primary"><Sparkles size={13} /> Design yours <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {data.related?.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="overline-v2">You may also love</div>
          <h2 className="mt-4 font-serif-warm text-3xl md:text-4xl">More from the same bench.</h2>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {data.related.map((r) => <V2ProductCard key={r.id} product={r} testIdPrefix="v2-related" />)}
          </div>
        </section>
      )}

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-[#fbf9f5] border-t border-[#e4ded2] p-3 flex gap-3">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#7a7466]">₹{price.toLocaleString("en-IN")}{variant ? ` · ${variant.size} ft` : ""}</div>
          <div className="font-serif-warm text-[15px] leading-tight text-[#1a1a1a] truncate">{p.name}</div>
        </div>
        <button onClick={onAdd} className="v2-btn-primary flex-shrink-0"><ShoppingBag size={13} /> Add</button>
      </div>
    </div>
  );
}
